import { compareRetailerPreference } from '../domain/retailerPreference';
// PoleSelection.js
import React, { useState, useEffect, useCallback } from 'react';
import '../FishFinderBuilder.css';
import { polesData } from '../data/polesData';
import ImageModal from './ImageModal';
import styled from 'styled-components';
import FindBestPriceButton from './PriceComparison/FindBestPriceButton';
import { fetchProductPrices, fetchProductNamesWithOffers, resolveProductNameForSupabase } from '../utils/api';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 80px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;

  @media (min-width: 769px) {
    height: 100px;
  }
`;

const ProductImage = styled.img`
  width: 85%;
  height: 85%;
  object-fit: contain;

  @media (min-width: 769px) {
    width: 95%;
    height: 95%;
  }
`;

const PoleSelection = ({
  pole = null,
  setPole = () => {},
  prevStep = () => {},
  nextStep = () => {},
  resetAll = () => {},
  jumpToStep = () => {},
  isInModal = false,
  onNavigateToFullPage = () => {},
  isInSelectionFlow = false
}) => {
  const [failedImages, setFailedImages] = useState({});
  const [modalImage, setModalImage] = useState({
    isOpen: false,
    url: '',
    alt: ''
  });
  // Initialize state from localStorage
  const initializeFromLocalStorage = () => {
    // Set BeatDown Outdoors defaults
    const beatdownDefaults = {
      selectedPoleId: pole?.id || null,
      expandedCards: new Set(),
      lengthPreferences: {},
      transducerPreferences: {},
      armPreferences: {
        "beatdown-pole-selector": "all-terrain"  // Default to All-Terrain
      },
      mountPreferences: {
        "beatdown-pole-selector": "track"        // Default to Track Mount
      },
      poleTypePreferences: {
        "beatdown-pole-selector": "breakaway"    // Default to BreakAway
      }
    };

    try {
      const saved = localStorage.getItem('poleSelectionPreferences');
      if (saved) {
        const preferences = JSON.parse(saved);
        return {
          selectedPoleId: preferences.selectedPoleId || beatdownDefaults.selectedPoleId,
          expandedCards: new Set(preferences.expandedCards || []),
          lengthPreferences: { ...beatdownDefaults.lengthPreferences, ...preferences.lengthPreferences },
          transducerPreferences: { ...beatdownDefaults.transducerPreferences, ...preferences.transducerPreferences },
          armPreferences: { ...beatdownDefaults.armPreferences, ...preferences.armPreferences },
          mountPreferences: { ...beatdownDefaults.mountPreferences, ...preferences.mountPreferences },
          poleTypePreferences: { ...beatdownDefaults.poleTypePreferences, ...preferences.poleTypePreferences }
        };
      }
    } catch (error) {
      console.warn('Failed to load pole preferences from localStorage:', error);
    }
    
    return beatdownDefaults;
  };

  const initialState = initializeFromLocalStorage();
  
  const [selectedPoleId, setSelectedPoleId] = useState(initialState.selectedPoleId);
  const [expandedCards, setExpandedCards] = useState(initialState.expandedCards);
  const [lengthPreferences, setLengthPreferences] = useState(initialState.lengthPreferences); // Store length preferences per pole
  const [transducerPreferences, setTransducerPreferences] = useState(initialState.transducerPreferences); // Store transducer preferences per pole
  const [armPreferences, setArmPreferences] = useState(initialState.armPreferences); // Store arm preferences per pole
  const [mountPreferences, setMountPreferences] = useState(initialState.mountPreferences); // Store mount preferences per pole
  const [poleTypePreferences, setPoleTypePreferences] = useState(initialState.poleTypePreferences); // Store pole type preferences per pole
  const [showInvalidCombinationDialog, setShowInvalidCombinationDialog] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [polePricing, setPolePricing] = useState({}); // Store pricing data for poles
  const [productNamesWithOffers, setProductNamesWithOffers] = useState(null);
  useEffect(() => {
    fetchProductNamesWithOffers().then(setProductNamesWithOffers);
  }, []);

  // Only show poles that have at least one active offer (hide HD Innovations etc. when no other retailers)
  const poleHasActiveOffer = useCallback((pole) => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0) return true;
    if (pole.csvLookupId && productNamesWithOffers.has(resolveProductNameForSupabase(pole.csvLookupId))) return true;
    if (pole.id === 'beatdown-pole-selector') {
      const armOpts = pole.armOptions ? Object.keys(pole.armOptions) : [];
      const mountOpts = pole.mountOptions ? Object.keys(pole.mountOptions) : [];
      const poleTypeOpts = pole.poleTypeOptions ? Object.keys(pole.poleTypeOptions) : [];
      for (const arm of armOpts) {
        for (const mount of mountOpts) {
          for (const pt of poleTypeOpts) {
            const sid = `${pt}-pole-${mount === 'track' ? 'track' : 'flat'}-${arm}`;
            const sp = polesData.find(p => p.id === sid);
            if (sp?.csvLookupId && productNamesWithOffers.has(resolveProductNameForSupabase(sp.csvLookupId))) return true;
          }
        }
      }
      return false;
    }
    if (pole.id === 'hdi-carbon-open-water-pole') {
      const lengthOpts = pole.lengthOptions ? Object.keys(pole.lengthOptions) : [];
      const transducerOpts = pole.transducerOptions ? Object.keys(pole.transducerOptions) : [];
      for (const len of lengthOpts) {
        for (const tr of transducerOpts) {
          const sid = `hdi-carbon-${len}-${tr.toLowerCase()}`;
          const sp = polesData.find(p => p.id === sid);
          if (sp?.csvLookupId && productNamesWithOffers.has(resolveProductNameForSupabase(sp.csvLookupId))) return true;
        }
      }
      return false;
    }
    return false;
  }, [productNamesWithOffers]);

  // Save preferences to localStorage
  const saveToLocalStorage = useCallback(() => {
    try {
      const preferences = {
        selectedPoleId,
        expandedCards: Array.from(expandedCards),
        lengthPreferences,
        transducerPreferences,
        armPreferences,
        mountPreferences,
        poleTypePreferences
      };
      localStorage.setItem('poleSelectionPreferences', JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save pole preferences to localStorage:', error);
    }
  }, [selectedPoleId, expandedCards, lengthPreferences, transducerPreferences, armPreferences, mountPreferences, poleTypePreferences]);

  // Save to localStorage whenever preferences change
  useEffect(() => {
    saveToLocalStorage();
  }, [saveToLocalStorage]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize state from existing pole selection
  useEffect(() => {
    if (pole && pole.id) {
      // Set the selected pole ID
      setSelectedPoleId(pole.id);
      
      // Set the length preference if it exists
      if (pole.lengthPreference) {
        setLengthPreferences(prev => ({
          ...prev,
          [pole.id]: pole.lengthPreference
        }));
      }
      
      // Set the transducer preference if it exists
      if (pole.transducerPreference) {
        setTransducerPreferences(prev => ({
          ...prev,
          [pole.id]: pole.transducerPreference
        }));
      }
      
      // Set the arm preference if it exists
      if (pole.armPreference) {
        setArmPreferences(prev => ({
          ...prev,
          [pole.id]: pole.armPreference
        }));
      }
      
      // Set the mount preference if it exists
      if (pole.mountPreference) {
        setMountPreferences(prev => ({
          ...prev,
          [pole.id]: pole.mountPreference
        }));
      }
      
      // Set the pole type preference if it exists
      if (pole.poleTypePreference) {
        setPoleTypePreferences(prev => ({
          ...prev,
          [pole.id]: pole.poleTypePreference
        }));
      }
    }
  }, [pole]);

  // Helper function to get specific product for current configuration
  const getSpecificProduct = useCallback((poleOption) => {
    if (poleOption.id === "beatdown-pole-selector") {
      // For BeatDown configurable pole, generate specific product ID
      const armPref = armPreferences[poleOption.id] || 'all-terrain';
      const mountPref = mountPreferences[poleOption.id] || 'track';
      const poleTypePref = poleTypePreferences[poleOption.id] || 'breakaway';

      // Generate specific product ID
      const specificProductId = `${poleTypePref}-pole-${mountPref === 'track' ? 'track' : 'flat'}-${armPref}`;
      
      // Find the specific product in polesData
      const specificProduct = polesData.find(p => p.id === specificProductId);
      return specificProduct || poleOption;
    } else if (poleOption.id === "hdi-carbon-open-water-pole") {
      // For HD Innovations configurable pole, generate specific product ID
      const lengthPref = lengthPreferences[poleOption.id] || '18-36';
      const transducerPref = transducerPreferences[poleOption.id] || 'LVS34';

      // Generate specific product ID
      const specificProductId = `hdi-carbon-${lengthPref}-${transducerPref.toLowerCase()}`;
      
      // Find the specific product in polesData
      const specificProduct = polesData.find(p => p.id === specificProductId);
      return specificProduct || poleOption;
    } else {
      return poleOption; // Non-configurable poles return themselves
    }
  }, [armPreferences, mountPreferences, poleTypePreferences, lengthPreferences, transducerPreferences]);

  // Fetch pricing data for specific products based on current configurations
  useEffect(() => {
    const fetchPolePricesForConfigurations = async () => {
      const configurationsToFetch = new Set();
      
      // For each pole, determine what specific products we need pricing for
      for (const poleOption of polesData) {
        if (poleOption.id === "beatdown-pole-selector") {
          // Generate all possible combinations for BeatDown selector
          const armOptions = poleOption.armOptions ? Object.keys(poleOption.armOptions) : ['all-terrain'];
          const mountOptions = poleOption.mountOptions ? Object.keys(poleOption.mountOptions) : ['track'];
          const poleTypeOptions = poleOption.poleTypeOptions ? Object.keys(poleOption.poleTypeOptions) : ['breakaway'];
          
          for (const armType of armOptions) {
            for (const mountType of mountOptions) {
              for (const poleType of poleTypeOptions) {
                const specificProductId = `${poleType}-pole-${mountType === 'track' ? 'track' : 'flat'}-${armType}`;
                const specificProduct = polesData.find(p => p.id === specificProductId);
                if (specificProduct && specificProduct.csvLookupId) {
                  configurationsToFetch.add(specificProduct);
                }
              }
            }
          }
        } else if (poleOption.id === "hdi-carbon-open-water-pole") {
          // Generate all possible combinations for HD Innovations selector
          const lengthOptions = poleOption.lengthOptions ? Object.keys(poleOption.lengthOptions) : ['18-36'];
          const transducerOptions = poleOption.transducerOptions ? Object.keys(poleOption.transducerOptions) : ['LVS34'];
          
          for (const lengthType of lengthOptions) {
            for (const transducerType of transducerOptions) {
              const specificProductId = `hdi-carbon-${lengthType}-${transducerType.toLowerCase()}`;
              const specificProduct = polesData.find(p => p.id === specificProductId);
              if (specificProduct && specificProduct.csvLookupId) {
                configurationsToFetch.add(specificProduct);
              }
            }
          }
        } else if (poleOption.csvLookupId) {
          configurationsToFetch.add(poleOption);
        }
      }

      // Fetch pricing for all specific products
      for (const product of configurationsToFetch) {
        try {
          const result = await fetchProductPrices(
            product.id,
            0,
            3,
            product.csvLookupId,
            true // exact match only
          );
          
          if (result.success && result.data.length > 0) {
            const sortedData = result.data.sort((a, b) => {
              const priceA = parseFloat(a.Price?.replace(/[$,]/g, '') || '0');
              const priceB = parseFloat(b.Price?.replace(/[$,]/g, '') || '0');
              return priceA - priceB || compareRetailerPreference(a.Source, b.Source);
            });
            
            const lowestPrice = parseFloat(sortedData[0].Price?.replace(/[$,]/g, '') || '0');
            
            // Calculate savings vs specific product MSRP
            const msrpSavings = product.msrp && lowestPrice < product.msrp 
              ? {
                  amount: (product.msrp - lowestPrice).toFixed(2),
                  percent: Math.round(((product.msrp - lowestPrice) / product.msrp) * 100),
                  reference: 'MSRP'
                }
              : null;
            
            setPolePricing(prev => ({
              ...prev,
              [product.id]: {
                price: lowestPrice.toFixed(2),
                msrpSavings: msrpSavings,
                source: sortedData[0].Source,
                lastUpdated: new Date().toISOString()
              }
            }));
          }
        } catch (error) {
          // Check if it's just a "no pricing data" error (expected for some products)
          if (error.message?.includes('No pricing data found')) {
            console.log(`⚠️ No pricing data available for ${product.name} (${product.id}) - this is expected for some products`);
          } else {
            console.error(`Error fetching prices for product ${product.id}:`, error);
          }
        }
      }
    };

    fetchPolePricesForConfigurations();
  }, []);

  // Helper function to format price
  const formatPrice = (price) => {
    if (!price) return '0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return numPrice.toFixed(2);
  };

  // Helper function to get pricing data for current configuration
  const getPricingForConfiguration = useCallback((poleOption) => {
    const specificProduct = getSpecificProduct(poleOption);
    return polePricing[specificProduct.id];
  }, [getSpecificProduct, polePricing]);

  const handleImageError = (e, poleName) => {
    if (failedImages[poleName]) return;
    
    console.error(`Image failed to load for ${poleName}: ${e.target.src}`);
    setFailedImages(prev => ({...prev, [poleName]: true}));
    
    const poleData = polesData.find(p => p.name === poleName);
    
    if (poleData && poleData.fallbackImage) {
      e.target.src = poleData.fallbackImage;
      return;
    }
    
    e.target.src = `https://dummyimage.com/150x100/cccccc/000000&text=${poleName.replace(/\s+/g, '+')}`;
    e.target.onerror = null;
  };

  // Helper function to get the display name for any pole
  const getDisplayName = useCallback((poleOption) => {
    // For configurable poles (BeatDown and HD Innovations), use the specific product name based on toggles
    if (poleOption.id === "beatdown-pole-selector" || poleOption.id === "hdi-carbon-open-water-pole") {
      const specificProduct = getSpecificProduct(poleOption);
      return specificProduct ? specificProduct.name : poleOption.name;
    }
    
    // For other poles, just use their name directly
    return poleOption.name;
  }, [getSpecificProduct]);



  const handlePoleSelect = useCallback((selectedPole) => {
    setSelectedPoleId(selectedPole.id);
    
    // For configurable poles (BeatDown and HD Innovations), use the specific product's actual data directly
    if (selectedPole.id === "beatdown-pole-selector" || selectedPole.id === "hdi-carbon-open-water-pole") {
      const specificProduct = getSpecificProduct(selectedPole);
      if (specificProduct) {
        // Use the specific product's actual data completely
        setPole({
          ...specificProduct, // Use all the specific product's properties (name, msrp, image, etc.)
          // Keep the original pole selector ID for UI logic
          originalPoleId: selectedPole.id,
          // Keep the preference tracking for the UI
          lengthPreference: lengthPreferences[selectedPole.id],
          transducerPreference: transducerPreferences[selectedPole.id],
          armPreference: armPreferences[selectedPole.id],
          mountPreference: mountPreferences[selectedPole.id],
          poleTypePreference: poleTypePreferences[selectedPole.id]
        });
      } else {
        // Fallback if specific product not found
        setPole(selectedPole);
      }
    } else {
      // For other poles, just use their data directly
      setPole(selectedPole);
    }
  }, [lengthPreferences, transducerPreferences, armPreferences, mountPreferences, poleTypePreferences, setPole, getSpecificProduct]);

  // Clear localStorage selectedPoleId if no pole is currently selected (user deleted it)
  useEffect(() => {
    if (!pole && selectedPoleId) {
      // User has deleted their pole selection, clear the stored selection
      setSelectedPoleId(null);
    }
  }, [pole, selectedPoleId]);

  // Restore saved pole selection from localStorage after helper functions are defined
  // Only do this if we're not in a selection flow context (where setPole would navigate away)
  useEffect(() => {
    if (selectedPoleId && selectedPoleId !== pole?.id && pole && !isInSelectionFlow) {
      const savedPole = polesData.find(p => p.id === selectedPoleId);
      if (savedPole) {
        handlePoleSelect(savedPole);
      }
    }
  }, [selectedPoleId, pole?.id, handlePoleSelect, pole, isInSelectionFlow]);

  // Function to toggle card expansion
  const toggleCardExpansion = (poleId, event) => {
    event.stopPropagation();
    
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(poleId)) {
        newSet.delete(poleId);
      } else {
        newSet.add(poleId);
      }
      return newSet;
    });
  };

  // For BeatDown pole: only (poleType, mount, arm) combos that have an offer
  const beatdownCombosWithOffers = useCallback((pole) => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0) return [];
    if (pole.id !== 'beatdown-pole-selector') return [];
    const poleTypeOpts = pole.poleTypeOptions ? Object.keys(pole.poleTypeOptions) : [];
    const mountOpts = pole.mountOptions ? Object.keys(pole.mountOptions) : [];
    const armOpts = pole.armOptions ? Object.keys(pole.armOptions) : [];
    const combos = [];
    poleTypeOpts.forEach((pt) => {
      mountOpts.forEach((mount) => {
        armOpts.forEach((arm) => {
          const specificProductId = `${pt}-pole-${mount === 'track' ? 'track' : 'flat'}-${arm}`;
          const sp = polesData.find(p => p.id === specificProductId);
          if (sp?.csvLookupId && productNamesWithOffers.has(resolveProductNameForSupabase(sp.csvLookupId))) {
            const rawName = sp.name || `${pole.poleTypeOptions[pt]?.name} + ${pole.mountOptions[mount]?.name} + ${pole.armOptions[arm]?.name}`;
            const label = rawName.replace(/^BeatDown\s+/i, '').trim() || rawName;
            combos.push({
              poleTypeKey: pt,
              mountKey: mount,
              armKey: arm,
              label
            });
          }
        });
      });
    });
    return combos;
  }, [productNamesWithOffers]);

  // For HDI pole: only (length, transducer) combos that have an offer – so user can't select e.g. 18-36 + LVS32 when that combo has no offer
  const hdiCombosWithOffers = useCallback((pole) => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0) return [];
    if (pole.id !== 'hdi-carbon-open-water-pole') return [];
    const lengthOpts = pole.lengthOptions ? Object.keys(pole.lengthOptions) : [];
    const transducerOpts = pole.transducerOptions ? Object.keys(pole.transducerOptions) : [];
    const combos = [];
    lengthOpts.forEach((len) => {
      transducerOpts.forEach((tr) => {
        const sp = polesData.find(p => p.id === `hdi-carbon-${len}-${tr.toLowerCase()}`);
        if (sp?.csvLookupId && productNamesWithOffers.has(resolveProductNameForSupabase(sp.csvLookupId))) {
          combos.push({ lengthKey: len, transducerKey: tr, label: `${pole.lengthOptions[len]?.name || len} ${pole.transducerOptions[tr]?.name || tr}` });
        }
      });
    });
    return combos;
  }, [productNamesWithOffers]);

  // When offers load, if HDI pole current selection has no offer (e.g. 18-36 LVS32), switch to first available combo
  const hdiPole = polesData.find(p => p.id === 'hdi-carbon-open-water-pole');
  useEffect(() => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0 || !hdiPole) return;
    const combos = hdiCombosWithOffers(hdiPole);
    if (combos.length === 0) return;
    const currentLen = lengthPreferences['hdi-carbon-open-water-pole'] || '18-36';
    const currentTr = transducerPreferences['hdi-carbon-open-water-pole'] || 'LVS34';
    const sp = polesData.find(p => p.id === `hdi-carbon-${currentLen}-${currentTr.toLowerCase()}`);
    const hasOffer = sp?.csvLookupId && productNamesWithOffers.has(resolveProductNameForSupabase(sp.csvLookupId));
    if (!hasOffer) {
      const first = combos[0];
      setLengthPreferences(prev => ({ ...prev, 'hdi-carbon-open-water-pole': first.lengthKey }));
      setTransducerPreferences(prev => ({ ...prev, 'hdi-carbon-open-water-pole': first.transducerKey }));
    }
  }, [productNamesWithOffers, hdiPole, hdiCombosWithOffers, lengthPreferences, transducerPreferences]);

  // When offers load, if BeatDown current selection has no offer, switch to first available combo
  const beatdownPole = polesData.find(p => p.id === 'beatdown-pole-selector');
  useEffect(() => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0 || !beatdownPole) return;
    const combos = beatdownCombosWithOffers(beatdownPole);
    if (combos.length === 0) return;
    const currentPt = poleTypePreferences['beatdown-pole-selector'] || 'breakaway';
    const currentMount = mountPreferences['beatdown-pole-selector'] || 'track';
    const currentArm = armPreferences['beatdown-pole-selector'] || 'all-terrain';
    const specificProductId = `${currentPt}-pole-${currentMount === 'track' ? 'track' : 'flat'}-${currentArm}`;
    const sp = polesData.find(p => p.id === specificProductId);
    const hasOffer = sp?.csvLookupId && productNamesWithOffers.has(resolveProductNameForSupabase(sp.csvLookupId));
    if (!hasOffer) {
      const first = combos[0];
      setPoleTypePreferences(prev => ({ ...prev, 'beatdown-pole-selector': first.poleTypeKey }));
      setMountPreferences(prev => ({ ...prev, 'beatdown-pole-selector': first.mountKey }));
      setArmPreferences(prev => ({ ...prev, 'beatdown-pole-selector': first.armKey }));
    }
  }, [productNamesWithOffers, beatdownPole, beatdownCombosWithOffers, poleTypePreferences, mountPreferences, armPreferences]);

  // Helper function to check if pole has length options
  const hasLengthOptions = (poleOption) => {
    return poleOption.lengthOptions && 
           Object.keys(poleOption.lengthOptions).length > 1;
  };

  // Helper function to check if pole has transducer options
  const hasTransducerOptions = (poleOption) => {
    return poleOption.transducerOptions && 
           Object.keys(poleOption.transducerOptions).length > 1;
  };

  // Helper function to check if pole has arm options
  const hasArmOptions = (poleOption) => {
    return poleOption.armOptions && 
           Object.keys(poleOption.armOptions).length > 1;
  };

  // Helper function to check if pole has mount options
  const hasMountOptions = (poleOption) => {
    return poleOption.mountOptions && 
           Object.keys(poleOption.mountOptions).length > 1;
  };

  // Helper function to check if pole has pole type options
  const hasPoleTypeOptions = (poleOption) => {
    return poleOption.poleTypeOptions && 
           Object.keys(poleOption.poleTypeOptions).length > 1;
  };

  // Function to handle length preference change for individual pole
  const handleLengthPreferenceChange = (poleId, preference) => {
    setLengthPreferences(prev => ({
      ...prev,
      [poleId]: preference
    }));
  };

  // Function to handle transducer preference change for individual pole
  const handleTransducerPreferenceChange = (poleId, preference) => {
    setTransducerPreferences(prev => ({
      ...prev,
      [poleId]: preference
    }));
  };

  // Function to handle arm preference change for individual pole
  const handleArmPreferenceChange = (poleId, preference) => {
    const poleOption = polesData.find(p => p.id === poleId);
    const currentPoleType = poleTypePreferences[poleId] || (poleOption?.poleTypeOptions ? Object.keys(poleOption.poleTypeOptions)[0] : null);
    const currentMountType = mountPreferences[poleId] || (poleOption?.mountOptions ? Object.keys(poleOption.mountOptions)[0] : null);
    
    if (!isValidCombination(poleOption, currentPoleType, currentMountType, preference)) {
      // For BreakAway + Track Mount + Original, suggest changing to Deck Mount
      if (currentPoleType === "breakaway" && currentMountType === "track" && preference === "original") {
        setPendingSelection({
          type: 'armType',
          poleId,
          preference,
          conflictingOption: 'mountType',
          suggestedChange: 'Deck Mount',
          suggestedValue: 'flat',
          message: `The Original arm option isn't available with the BreakAway Track Mount, would you like to use the Deck Mount with the Original arm or keep the Track Mount with the All-Terrain arm?`
        });
        setShowInvalidCombinationDialog(true);
      }
      // For Fixed (Non-Pivoting) + Track Mount + All-Terrain, suggest changing to BreakAway or Deck Mount
      else if (currentPoleType === "fixed" && currentMountType === "track" && preference === "all-terrain") {
        setPendingSelection({
          type: 'armType',
          poleId,
          preference,
          conflictingOption: 'poleType',
          suggestedChange: 'BreakAway',
          suggestedValue: 'breakaway',
          message: `The Track Mount option isn't available with the Fixed (Non-Pivoting) function, would you like to have the BreakAway function with the Track Mount or the Deck Mount with the Fixed (Non-Pivoting) function?`
        });
        setShowInvalidCombinationDialog(true);
      }
      // For Fixed (Non-Pivoting) + Track Mount + Original, suggest changing to BreakAway or Deck Mount
      else if (currentPoleType === "fixed" && currentMountType === "track" && preference === "original") {
        setPendingSelection({
          type: 'armType',
          poleId,
          preference,
          conflictingOption: 'poleType',
          suggestedChange: 'BreakAway',
          suggestedValue: 'breakaway',
          message: `The Track Mount option isn't available with the Fixed (Non-Pivoting) function, would you like to have the BreakAway function with the Track Mount or the Deck Mount with the Fixed (Non-Pivoting) function?`
        });
        setShowInvalidCombinationDialog(true);
      }
    } else {
      setArmPreferences(prev => ({
        ...prev,
        [poleId]: preference
      }));
    }
  };

  // Function to handle mount preference change for individual pole
  const handleMountPreferenceChange = (poleId, preference) => {
    const poleOption = polesData.find(p => p.id === poleId);
    const currentPoleType = poleTypePreferences[poleId] || (poleOption?.poleTypeOptions ? Object.keys(poleOption.poleTypeOptions)[0] : null);
    const currentArmType = armPreferences[poleId] || (poleOption?.armOptions ? Object.keys(poleOption.armOptions)[0] : null);
    
    if (!isValidCombination(poleOption, currentPoleType, preference, currentArmType)) {
      // For BreakAway + Track Mount + Original, suggest changing to All-Terrain arm
      if (currentPoleType === "breakaway" && preference === "track" && currentArmType === "original") {
        setPendingSelection({
          type: 'mountType',
          poleId,
          preference,
          conflictingOption: 'armType',
          suggestedChange: 'All-Terrain',
          suggestedValue: 'all-terrain',
          message: `The Original arm option isn't available with the BreakAway Track Mount, would you like to use the All-Terrain arm with the Track Mount or switch to the Deck Mount with the Original arm?`
        });
        setShowInvalidCombinationDialog(true);
      }
      // For Fixed (Non-Pivoting) + Track Mount + All-Terrain, suggest changing to BreakAway or Deck Mount
      else if (currentPoleType === "fixed" && preference === "track" && currentArmType === "all-terrain") {
        setPendingSelection({
          type: 'mountType',
          poleId,
          preference,
          conflictingOption: 'poleType',
          suggestedChange: 'BreakAway',
          suggestedValue: 'breakaway',
          message: `The Track Mount option isn't available with the Fixed (Non-Pivoting) function, would you like to have the BreakAway function with the Track Mount or the Deck Mount with the Fixed (Non-Pivoting) function?`
        });
        setShowInvalidCombinationDialog(true);
      }
      // For Fixed (Non-Pivoting) + Track Mount + Original, suggest changing to BreakAway or Deck Mount
      else if (currentPoleType === "fixed" && preference === "track" && currentArmType === "original") {
        setPendingSelection({
          type: 'mountType',
          poleId,
          preference,
          conflictingOption: 'poleType',
          suggestedChange: 'BreakAway',
          suggestedValue: 'breakaway',
          message: `The Track Mount option isn't available with the Fixed (Non-Pivoting) function, would you like to have the BreakAway function with the Track Mount or the Deck Mount with the Fixed (Non-Pivoting) function?`
        });
        setShowInvalidCombinationDialog(true);
      }
    } else {
      setMountPreferences(prev => ({
        ...prev,
        [poleId]: preference
      }));
    }
  };

  // Function to handle pole type preference change for individual pole
  const handlePoleTypePreferenceChange = (poleId, preference) => {
    const poleOption = polesData.find(p => p.id === poleId);
    const currentMountType = mountPreferences[poleId] || (poleOption?.mountOptions ? Object.keys(poleOption.mountOptions)[0] : null);
    const currentArmType = armPreferences[poleId] || (poleOption?.armOptions ? Object.keys(poleOption.armOptions)[0] : null);
    
    if (!isValidCombination(poleOption, preference, currentMountType, currentArmType)) {
      // For BreakAway + Track Mount + Original, suggest changing to All-Terrain arm
      if (preference === "breakaway" && currentMountType === "track" && currentArmType === "original") {
        setPendingSelection({
          type: 'poleType',
          poleId,
          preference,
          conflictingOption: 'armType',
          suggestedChange: 'All-Terrain',
          suggestedValue: 'all-terrain',
          message: `The Original arm option isn't available with the BreakAway Track Mount, would you like to use the All-Terrain arm with the Track Mount or switch to the Deck Mount with the Original arm?`
        });
        setShowInvalidCombinationDialog(true);
      }
      // For Fixed (Non-Pivoting) + Track Mount + All-Terrain, suggest not using Fixed with Track Mount
      else if (preference === "fixed" && currentMountType === "track" && currentArmType === "all-terrain") {
        setPendingSelection({
          type: 'poleType',
          poleId,
          preference,
          conflictingOption: 'mountType',
          suggestedChange: 'Deck Mount',
          suggestedValue: 'flat',
          message: `The Track Mount option isn't available with the Fixed (Non-Pivoting) function, would you like to have the BreakAway function with the Track Mount or the Deck Mount with the Fixed (Non-Pivoting) function?`
        });
        setShowInvalidCombinationDialog(true);
      }
      // For Fixed (Non-Pivoting) + Track Mount + Original, suggest not using Fixed with Track Mount
      else if (preference === "fixed" && currentMountType === "track" && currentArmType === "original") {
        setPendingSelection({
          type: 'poleType',
          poleId,
          preference,
          conflictingOption: 'mountType',
          suggestedChange: 'Deck Mount',
          suggestedValue: 'flat',
          message: `The Track Mount option isn't available with the Fixed (Non-Pivoting) function, would you like to have the BreakAway function with the Track Mount or the Deck Mount with the Fixed (Non-Pivoting) function?`
        });
        setShowInvalidCombinationDialog(true);
      }
    } else {
      setPoleTypePreferences(prev => ({
        ...prev,
        [poleId]: preference
      }));
    }
  };

  // Function to check if a combination is valid for BeatDown Outdoors pole
  const isValidCombination = (poleOption, poleType, mountType, armType) => {
    // For BeatDown Outdoors pole, certain combinations are not available
    if (poleOption.id === "beatdown-pole-selector") {
      // BreakAway + Track Mount + Original is not available
      if (poleType === "breakaway" && mountType === "track" && armType === "original") {
        return false;
      }
      // Fixed (Non-Pivoting) + Track Mount + All-Terrain is not available
      if (poleType === "fixed" && mountType === "track" && armType === "all-terrain") {
        return false;
      }
      // Fixed (Non-Pivoting) + Track Mount + Original is not available
      if (poleType === "fixed" && mountType === "track" && armType === "original") {
        return false;
      }
    }
    return true;
  };



  // Function to handle dialog confirmation (user wants to apply suggested change)
  const handleConfirmInvalidCombination = () => {
    if (pendingSelection) {
      const { type, poleId, preference, conflictingOption, suggestedValue } = pendingSelection;
      
      // Apply the suggested change
      if (conflictingOption === 'poleType') {
        setPoleTypePreferences(prev => ({
          ...prev,
          [poleId]: suggestedValue
        }));
      } else if (conflictingOption === 'mountType') {
        setMountPreferences(prev => ({
          ...prev,
          [poleId]: suggestedValue
        }));
      } else if (conflictingOption === 'armType') {
        setArmPreferences(prev => ({
          ...prev,
          [poleId]: suggestedValue
        }));
      }
      
      // Also apply the original preference that user wanted (if not conflicting)
      if (type === 'poleType' && conflictingOption !== 'poleType') {
        setPoleTypePreferences(prev => ({
          ...prev,
          [poleId]: preference
        }));
      } else if (type === 'mountType' && conflictingOption !== 'mountType') {
        setMountPreferences(prev => ({
          ...prev,
          [poleId]: preference
        }));
      } else if (type === 'armType' && conflictingOption !== 'armType') {
        setArmPreferences(prev => ({
          ...prev,
          [poleId]: preference
        }));
      }
    }
    
    setShowInvalidCombinationDialog(false);
    setPendingSelection(null);
  };

  // Function to handle dialog cancellation
  const handleCancelInvalidCombination = () => {
    setShowInvalidCombinationDialog(false);
    setPendingSelection(null);
  };

  return (
    <div style={{ backgroundColor: '#000000', minHeight: '100vh', padding: '0 20px' }}>
      {/* Back Button */}
      <div style={{
        padding: '15px 0',
        width: '100%',
        marginLeft: '-20px'
      }}>
        <button
          onClick={prevStep}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(0, 0, 0, 0.4)',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '8px 16px',
            marginLeft: '20px',
            borderRadius: '4px',
            width: 'fit-content'
          }}
        >
          ← Back to Builder
        </button>
      </div>

      <h1 style={{ 
        color: '#ffffff',
        fontSize: '28px',
        marginBottom: '16px'
      }}>Pole Selection</h1>

      <GridContainer>
        {polesData
          .filter(pole => !pole.hiddenFromSelection && poleHasActiveOffer(pole)) // Only show poles with at least one active offer
          .map(poleOption => (
          <div 
            key={poleOption.id}
            style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.2s ease'
            }}
          >
            <ImageContainer onClick={() => setModalImage({ isOpen: true, url: poleOption.image, alt: getDisplayName(poleOption) })}>
              <ProductImage 
                src={poleOption.image} 
                alt={getDisplayName(poleOption)}
                onError={(e) => handleImageError(e, poleOption.name)}
              />
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>
            </ImageContainer>

            <div style={{ padding: '12px' }}>
                                      <h3 style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#fff' }}>
                          {getDisplayName(poleOption)}
                        </h3>
              {(() => {
                const specificProduct = getSpecificProduct(poleOption);
                const polePricingData = getPricingForConfiguration(poleOption);
                const hasLowestPrice = polePricingData && polePricingData.price;

                if (hasLowestPrice) {
                  return (
                    <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                      <p style={{ color: '#4CAF50', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                        Best Price: ${formatPrice(polePricingData.price)}
                      </p>
                      {polePricingData.msrpSavings && (
                        <div style={{
                          marginTop: '4px',
                          fontSize: '12px',
                          color: '#e67e22',
                          fontWeight: '500'
                        }}>
                          ↓ Save ${formatPrice(polePricingData.msrpSavings.amount)} off {polePricingData.msrpSavings.reference || 'MSRP'}
                        </div>
                      )}
                    </div>
                  );
                } else if (specificProduct.csvLookupId) {
                  // Show loading for products with CSV lookup ID
                  return (
                    <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                      <div style={{ fontSize: '0.8em', color: '#4CAF50', marginTop: '2px', display: 'flex', alignItems: 'center', gap: 4 }}>
                        📡 Loading best price...
                      </div>
                      <p style={{ color: '#b8c7d9', margin: '4px 0 0 0', fontSize: '0.8em' }}>
                        MSRP: ${formatPrice(specificProduct.msrp)}
                      </p>
                    </div>
                  );
                } else {
                  // Show MSRP for products without CSV lookup ID
                  return (
                    <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                      <p style={{ color: '#4CAF50', margin: '0 0 4px 0' }}>
                        MSRP: ${formatPrice(specificProduct.msrp)}
                      </p>
                    </div>
                  );
                }
              })()}

              {/* HDI pole: single "Configuration" list – only combos that have an offer (so 18-36 LVS32 etc. can't be selected) */}
              {poleOption.id === 'hdi-carbon-open-water-pole' && hasLengthOptions(poleOption) && (() => {
                const combos = hdiCombosWithOffers(poleOption);
                if (combos.length === 0) return null;
                const currentLen = lengthPreferences[poleOption.id] || '18-36';
                const currentTr = transducerPreferences[poleOption.id] || 'LVS34';
                const lenOpt = poleOption.lengthOptions?.[currentLen];
                const trOpt = poleOption.transducerOptions?.[currentTr];
                return (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.75em', color: '#b8c7d9', marginBottom: '4px' }}>Configuration:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {combos.map(({ lengthKey, transducerKey, label }) => (
                        <button
                          key={`${lengthKey}-${transducerKey}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLengthPreferenceChange(poleOption.id, lengthKey);
                            handleTransducerPreferenceChange(poleOption.id, transducerKey);
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '600',
                            backgroundColor: (currentLen === lengthKey && currentTr === transducerKey) ? '#4CAF50' : '#6c757d',
                            color: '#ffffff',
                            transition: 'all 0.2s ease',
                            textAlign: 'left'
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {/* Explanation for selected configuration */}
                    {(lenOpt?.usableLength || trOpt?.description) && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        fontSize: '0.7em',
                        color: '#b8c7d9',
                        lineHeight: '1.4'
                      }}>
                        {lenOpt?.usableLength && (
                          <div style={{ marginBottom: trOpt?.description ? '4px' : 0 }}>
                            <span style={{ fontWeight: '600', color: '#e0e0e0' }}>{lenOpt.name}:</span> Usable length {lenOpt.usableLength}
                          </div>
                        )}
                        {trOpt?.description && (
                          <div>
                            <span style={{ fontWeight: '600', color: '#e0e0e0' }}>{trOpt.name}:</span> {trOpt.description}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Length preference toggle for non-HDI poles with multiple options */}
              {hasLengthOptions(poleOption) && poleOption.id !== 'hdi-carbon-open-water-pole' && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '0.75em', color: '#b8c7d9', marginBottom: '4px' }}>Length Options:</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {Object.keys(poleOption.lengthOptions).map((key) => {
                      const option = poleOption.lengthOptions[key];
                      return (
                        <button
                          key={key}
                          onClick={(e) => { e.stopPropagation(); handleLengthPreferenceChange(poleOption.id, key); }}
                          style={{
                            flex: '1', minWidth: '60px', padding: '6px 8px', fontSize: '11px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: '600',
                            backgroundColor: (lengthPreferences[poleOption.id] || Object.keys(poleOption.lengthOptions)[0]) === key ? '#4CAF50' : '#6c757d',
                            color: '#ffffff', transition: 'all 0.2s ease'
                          }}
                        >
                          {option.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Transducer preference toggle for non-HDI poles with multiple options */}
              {hasTransducerOptions(poleOption) && poleOption.id !== 'hdi-carbon-open-water-pole' && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '0.75em', color: '#b8c7d9', marginBottom: '4px' }}>Transducer Type:</div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {Object.keys(poleOption.transducerOptions).map((key) => {
                      const option = poleOption.transducerOptions[key];
                      return (
                        <button
                          key={key}
                          onClick={(e) => { e.stopPropagation(); handleTransducerPreferenceChange(poleOption.id, key); }}
                          style={{
                            flex: '1 1 calc(50% - 2px)', minWidth: '80px', padding: '6px 8px', fontSize: '11px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: '600',
                            backgroundColor: (transducerPreferences[poleOption.id] || Object.keys(poleOption.transducerOptions)[0]) === key ? '#4CAF50' : '#6c757d',
                            color: '#ffffff', transition: 'all 0.2s ease'
                          }}
                        >
                          {option.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* BeatDown: single Configuration list – only combos that have an offer */}
              {poleOption.id === 'beatdown-pole-selector' && hasPoleTypeOptions(poleOption) && (() => {
                const combos = beatdownCombosWithOffers(poleOption);
                if (combos.length === 0) return null;
                const currentPt = poleTypePreferences[poleOption.id] || 'breakaway';
                const currentMount = mountPreferences[poleOption.id] || 'track';
                const currentArm = armPreferences[poleOption.id] || 'all-terrain';
                const ptOpt = poleOption.poleTypeOptions?.[currentPt];
                const mountOpt = poleOption.mountOptions?.[currentMount];
                const armOpt = poleOption.armOptions?.[currentArm];
                return (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.75em', color: '#b8c7d9', marginBottom: '4px' }}>Configuration:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {combos.map(({ poleTypeKey, mountKey, armKey, label }) => {
                        const isSelected = currentPt === poleTypeKey && currentMount === mountKey && currentArm === armKey;
                        return (
                          <button
                            key={`${poleTypeKey}-${mountKey}-${armKey}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPoleTypePreferences(prev => ({ ...prev, [poleOption.id]: poleTypeKey }));
                              setMountPreferences(prev => ({ ...prev, [poleOption.id]: mountKey }));
                              setArmPreferences(prev => ({ ...prev, [poleOption.id]: armKey }));
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              fontSize: '11px',
                              borderRadius: '4px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '600',
                              backgroundColor: isSelected ? '#4CAF50' : '#6c757d',
                              color: '#ffffff',
                              transition: 'all 0.2s ease',
                              textAlign: 'left'
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    {/* Explanation for selected configuration – what each option includes */}
                    {(ptOpt?.description || mountOpt?.description || armOpt?.description) && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        fontSize: '0.7em',
                        color: '#b8c7d9',
                        lineHeight: '1.4'
                      }}>
                        {ptOpt?.description && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#e0e0e0' }}>{ptOpt.name}:</span> {ptOpt.description}
                          </div>
                        )}
                        {mountOpt?.description && (
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#e0e0e0' }}>{mountOpt.name}:</span> {mountOpt.description}
                          </div>
                        )}
                        {armOpt?.description && (
                          <div>
                            <span style={{ fontWeight: '600', color: '#e0e0e0' }}>{armOpt.name}:</span> {armOpt.description}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Pole type / Mount / Arm toggles for non-BeatDown poles with multiple options */}
              {hasPoleTypeOptions(poleOption) && poleOption.id !== 'beatdown-pole-selector' && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '0.75em', color: '#b8c7d9', marginBottom: '4px' }}>Arm Type:</div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {Object.entries(poleOption.poleTypeOptions).map(([key, option]) => (
                      <button
                        key={key}
                        onClick={(e) => { e.stopPropagation(); handlePoleTypePreferenceChange(poleOption.id, key); }}
                        style={{
                          flex: '1 1 calc(50% - 2px)', minWidth: '70px', padding: '6px 8px', fontSize: '11px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: '600',
                          backgroundColor: (poleTypePreferences[poleOption.id] || Object.keys(poleOption.poleTypeOptions)[0]) === key ? '#4CAF50' : '#6c757d',
                          color: '#ffffff', transition: 'all 0.2s ease'
                        }}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {hasMountOptions(poleOption) && poleOption.id !== 'beatdown-pole-selector' && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '0.75em', color: '#b8c7d9', marginBottom: '4px' }}>Mount Type:</div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {Object.entries(poleOption.mountOptions).map(([key, option]) => (
                      <button
                        key={key}
                        onClick={(e) => { e.stopPropagation(); handleMountPreferenceChange(poleOption.id, key); }}
                        style={{
                          flex: '1 1 calc(50% - 2px)', minWidth: '70px', padding: '6px 8px', fontSize: '11px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: '600',
                          backgroundColor: (mountPreferences[poleOption.id] || Object.keys(poleOption.mountOptions)[0]) === key ? '#4CAF50' : '#6c757d',
                          color: '#ffffff', transition: 'all 0.2s ease'
                        }}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {hasArmOptions(poleOption) && poleOption.id !== 'beatdown-pole-selector' && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '0.75em', color: '#b8c7d9', marginBottom: '4px' }}>Pole Type:</div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {Object.entries(poleOption.armOptions).map(([key, option]) => (
                      <button
                        key={key}
                        onClick={(e) => { e.stopPropagation(); handleArmPreferenceChange(poleOption.id, key); }}
                        style={{
                          flex: '1 1 calc(50% - 2px)', minWidth: '70px', padding: '6px 8px', fontSize: '11px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: '600',
                          backgroundColor: (armPreferences[poleOption.id] || Object.keys(poleOption.armOptions)[0]) === key ? '#4CAF50' : '#6c757d',
                          color: '#ffffff', transition: 'all 0.2s ease'
                        }}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed specs section */}
              <div className="pole-specs" style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '0',
                fontSize: '0.9em',
                marginBottom: '10px'
              }}>
                {/* Descriptive section with best for badge and benefits */}
                <div style={{ marginBottom: 10, fontSize: '0.82em' }}>
                  <div style={{ fontWeight: 700, fontSize: '1em', marginBottom: 2 }}>
                    {poleOption.bestFor ? `Best for ${poleOption.bestFor}` : 'Reliable Performance'}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    {poleOption.description}
                  </div>
                  {poleOption.benefits && (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {poleOption.benefits.map((benefit, index) => (
                        <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                          <span style={{ color: '#22c55e', fontSize: '1.2em', marginRight: 6 }}>✅</span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Key specs */}
                <div className="spec-row" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 2px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Construction</span>
                  <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.construction || 'Not specified'}</span>
                </div>
                <div className="spec-row" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 2px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Type</span>
                  <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.poleType || 'Not specified'}</span>
                </div>
                <div className="spec-row" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 2px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Weight</span>
                  <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.weight || 'Not specified'}</span>
                </div>
                
                {/* Show more specs when expanded */}
                {expandedCards.has(poleOption.id) ? (
                  <>
                    {/* Show selected length usable range if applicable */}
                    {lengthPreferences[poleOption.id] && poleOption.lengthOptions?.[lengthPreferences[poleOption.id]]?.usableLength && (
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Usable Length</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.lengthOptions[lengthPreferences[poleOption.id]].usableLength}</span>
                      </div>
                    )}
                    {poleOption.poleDiameters && (
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Pole Diameters</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.poleDiameters}</span>
                      </div>
                    )}
                    {poleOption.mountType && (
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Mount Type</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.mountType}</span>
                      </div>
                    )}
                    {poleOption.lockingSystem && (
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Locking System</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.lockingSystem}</span>
                      </div>
                    )}
                    {poleOption.compatibility && (
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Compatibility</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.compatibility}</span>
                      </div>
                    )}
                    {poleOption.included && (
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Included</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.included}</span>
                      </div>
                    )}
                  </>
                ) : (
                  // Show a faded version of additional specs when not expanded
                  <div className="spec-row" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 2px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    opacity: 0.6,
                    position: 'relative'
                  }}>
                    <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Mount Type</span>
                    <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{poleOption.mountType || 'See more specs'}</span>
                    {/* Gradient overlay to fade the content */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to bottom, rgba(26, 26, 26, 0) 0%, rgba(26, 26, 26, 0.8) 100%)',
                      pointerEvents: 'none'
                    }} />
                  </div>
                )}
                
                {/* Show/Hide button */}
                <button
                  onClick={(e) => toggleCardExpansion(poleOption.id, e)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#b8c7d9',
                    width: '100%',
                    padding: '4px 0',
                    marginTop: '4px',
                    fontSize: '0.9em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {expandedCards.has(poleOption.id) ? (
                    <>Show Less <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▲</span></>
                  ) : (
                    <>Show More Specs <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▼</span></>
                  )}
                </button>
              </div>

              {/* Compact Styled Action Buttons */}
              <div style={{
                marginTop: '8px',
                padding: '6px',
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '6px',
                  color: '#ffffff',
                  fontSize: '0.6em',
                  fontWeight: '600',
                  opacity: 0.9,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  Choose Your Next Step:
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {/* Find Best Price Button */}
                  {(() => {
                    // Get the specific product for this pole configuration
                    const specificProduct = getSpecificProduct(poleOption);
                    
                    // Only show Find Best Price button if we have a specific product with CSV lookup ID
                    if (!specificProduct || !specificProduct.csvLookupId) {
                      return null;
                    }
                    
                    return (
                      <FindBestPriceButton
                        productId={specificProduct.csvLookupId}
                        productType="pole"
                        style={{
                          width: '100%',
                          fontSize: '0.75em',
                          padding: '6px 8px',
                          background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                          border: 'none',
                          color: 'white',
                          borderRadius: '4px',
                          fontWeight: '600',
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                          margin: '0',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textDecoration: 'none'
                        }}
                        text={
                          <div style={{ 
                            textAlign: 'center', 
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            gap: '2px'
                          }}>
                            <div style={{ lineHeight: '1.1' }}>💰 Find Best Price</div>
                            <div style={{
                              fontSize: '0.85em',
                              fontWeight: '400',
                              opacity: 0.9,
                              lineHeight: '1.1'
                            }}>
                              Compare prices across stores
                            </div>
                          </div>
                        }
                      />
                    );
                  })()}
                  
                  {/* OR divider - only show if Find Best Price button is shown */}
                  {(() => {
                    const specificProduct = getSpecificProduct(poleOption);
                    if (!specificProduct || !specificProduct.csvLookupId) {
                      return null;
                    }
                    
                    return (
                      <div style={{
                        textAlign: 'center',
                        fontSize: '0.55em',
                        color: '#aaa',
                        margin: '0',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        or
                      </div>
                    );
                  })()}
                  
                  {/* Select & Build Setup Button */}
                  <button 
                     onClick={() => handlePoleSelect(poleOption)}
                     style={{
                       width: '100%',
                       padding: '6px 8px',
                       fontSize: '0.75em',
                       background: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
                       border: 'none',
                       color: 'white',
                       borderRadius: '4px',
                       cursor: 'pointer',
                       fontWeight: '600',
                       transition: 'all 0.2s ease',
                       textAlign: 'center',
                       display: 'flex',
                       flexDirection: 'column',
                       alignItems: 'center',
                       justifyContent: 'center',
                       gap: '2px'
                     }}
                     onMouseEnter={(e) => {
                       e.target.style.background = 'linear-gradient(135deg, #0077dd 0%, #0055aa 100%)';
                       e.target.style.transform = 'translateY(-1px)';
                     }}
                     onMouseLeave={(e) => {
                       e.target.style.background = 'linear-gradient(135deg, #0066cc 0%, #004499 100%)';
                       e.target.style.transform = 'translateY(0)';
                     }}
                   >
                     <div style={{ lineHeight: '1.1' }}>
                       🔧 Select & Build Setup
                     </div>
                     <div style={{
                       fontSize: '0.85em',
                       fontWeight: '400',
                       opacity: 0.9,
                       lineHeight: '1.1'
                     }}>
                       Add screen, transducer, etc.
                     </div>
                   </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </GridContainer>

      {modalImage.isOpen && (
        <ImageModal
          isOpen={modalImage.isOpen}
          imageUrl={modalImage.url}
          altText={modalImage.alt}
          onClose={() => setModalImage({ isOpen: false, url: '', alt: '' })}
        />
      )}

      {/* Invalid Combination Confirmation Dialog */}
      {showInvalidCombinationDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            color: '#ffffff'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              color: '#ff6b35',
              fontSize: '1.2em'
            }}>
              ⚠️ Invalid Combination
            </h3>
            
            <p style={{
              margin: '0 0 24px 0',
              lineHeight: '1.5',
              color: '#b8c7d9'
            }}>
              {pendingSelection?.message}
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              flexWrap: 'nowrap'
            }}>
              <button
                onClick={handleCancelInvalidCombination}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  border: '1px solid #6c757d',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#6c757d';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                Cancel
              </button>
              
              {/* For Fixed + Track Mount combinations, show both options */}
              {pendingSelection?.message?.includes("Track Mount option isn't available with the Fixed (Non-Pivoting) function") ? (
                <>
                  <button
                    onClick={() => {
                      // Change to BreakAway + Track Mount
                      if (pendingSelection) {
                        const { poleId } = pendingSelection;
                        setPoleTypePreferences(prev => ({
                          ...prev,
                          [poleId]: 'breakaway'
                        }));
                        setMountPreferences(prev => ({
                          ...prev,
                          [poleId]: 'track'
                        }));
                      }
                      setShowInvalidCombinationDialog(false);
                      setPendingSelection(null);
                    }}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: '#4CAF50',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#45a049';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#4CAF50';
                    }}
                  >
                    BreakAway + Track Mount
                  </button>
                  
                  <button
                    onClick={() => {
                      // Change to Fixed + Deck Mount
                      if (pendingSelection) {
                        const { poleId } = pendingSelection;
                        setPoleTypePreferences(prev => ({
                          ...prev,
                          [poleId]: 'fixed'
                        }));
                        setMountPreferences(prev => ({
                          ...prev,
                          [poleId]: 'flat'
                        }));
                      }
                      setShowInvalidCombinationDialog(false);
                      setPendingSelection(null);
                    }}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: '#2196F3',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#1976D2';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#2196F3';
                    }}
                  >
                    Fixed (Non-Pivoting) + Deck Mount
                  </button>
                </>
              ) : pendingSelection?.message?.includes("Original arm option isn't available with the BreakAway Track Mount") ? (
                /* For BreakAway + Track Mount + Original combinations, show both options */
                <>
                  <button
                    onClick={() => {
                      // Change to Track Mount + All-Terrain
                      if (pendingSelection) {
                        const { poleId } = pendingSelection;
                        setMountPreferences(prev => ({
                          ...prev,
                          [poleId]: 'track'
                        }));
                        setArmPreferences(prev => ({
                          ...prev,
                          [poleId]: 'all-terrain'
                        }));
                      }
                      setShowInvalidCombinationDialog(false);
                      setPendingSelection(null);
                    }}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: '#4CAF50',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#45a049';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#4CAF50';
                    }}
                  >
                    Track Mount + All-Terrain
                  </button>
                  
                  <button
                    onClick={() => {
                      // Change to Deck Mount + Original
                      if (pendingSelection) {
                        const { poleId } = pendingSelection;
                        setMountPreferences(prev => ({
                          ...prev,
                          [poleId]: 'flat'
                        }));
                        setArmPreferences(prev => ({
                          ...prev,
                          [poleId]: 'original'
                        }));
                      }
                      setShowInvalidCombinationDialog(false);
                      setPendingSelection(null);
                    }}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: '#2196F3',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#1976D2';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#2196F3';
                    }}
                  >
                    Deck Mount + Original
                  </button>
                </>
              ) : (
                /* For other invalid combinations, show the original single button */
                <button
                  onClick={handleConfirmInvalidCombination}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#4CAF50',
                    color: '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#45a049';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#4CAF50';
                  }}
                >
                  Change to {pendingSelection?.suggestedChange}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoleSelection;
